import {Box, Typography} from '@mui/material';
import axios, {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {CortileAuthLayout} from './CortileAuthLayout';
import {
  FieldError,
  Form,
  PrimaryButton,
  SecondaryButton,
  StepHeading,
  Strong,
  TextButton,
  TextField,
} from './CortileFormParts';
import {CORTILE, FONT} from './tokens';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_PAUSE_MS = 10000;

const requestReset = (email: string) =>
  axios.post(`${import.meta.env.VITE_API_ROOT}/auth/users/reset_password/`, {
    email,
  });

export const CortileForgotPassword = () => {
  const navigate = useNavigate();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    if (!justResent) return;
    const timer = window.setTimeout(
      () => setJustResent(false),
      RESEND_PAUSE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [justResent]);
  const {
    register,
    handleSubmit,
    setError,
    formState: {errors, isSubmitting},
  } = useForm<{email: string}>();

  const onSubmit = handleSubmit(async values => {
    const email = values.email.trim().toLowerCase();
    try {
      await requestReset(email);
      setSentTo(email);
    } catch (error) {
      const message =
        (isAxiosError(error) && error.response?.data?.email?.[0]) ||
        'Something went wrong, please try again';
      setError('email', {message});
    }
  });

  const resend = async () => {
    if (!sentTo || resending || justResent) return;
    setResendError(null);
    setResending(true);
    try {
      await requestReset(sentTo);
      setJustResent(true);
      enqueueSnackbar('Reset link sent again', {variant: 'success'});
    } catch {
      setResendError('We could not resend the email, please try again');
    } finally {
      setResending(false);
    }
  };

  return (
    <CortileAuthLayout stepKey={sentTo ? 'sent' : 'forgot'}>
      {!sentTo && (
        <>
          <StepHeading title="Reset your password">
            Enter the email address you use for Cortile. We&rsquo;ll send you a
            link to set a new password.
          </StepHeading>
          <Form onSubmit={onSubmit}>
            <TextField
              label="Email address"
              data-attr="cortile-login-reset-email-input"
              type="email"
              autoComplete="username"
              placeholder="name@agency.co.uk"
              error={errors.email?.message}
              {...register('email', {
                required: 'Enter a valid email address',
                pattern: {
                  value: EMAIL_PATTERN,
                  message: 'Enter a valid email address',
                },
              })}
            />
            <PrimaryButton
              type="submit"
              loading={isSubmitting}
              data-attr="cortile-login-reset-submit"
            >
              Send reset link
            </PrimaryButton>
            <TextButton
              back
              onClick={() => navigate('/login')}
              data-attr="cortile-login-reset-back"
            >
              Back to log in
            </TextButton>
          </Form>
        </>
      )}
      {sentTo && (
        <>
          <StepHeading title="Check your email">
            If <Strong>{sentTo}</Strong> has a Cortile account, we&rsquo;ve sent
            it a link to reset your password.
          </StepHeading>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              pt: 2.5,
              borderTop: `1px solid ${CORTILE.divider}`,
            }}
          >
            <Typography
              sx={{m: 0, fontFamily: FONT, fontSize: 14, color: CORTILE.muted}}
            >
              Nothing there? Check your spam folder, or
            </Typography>
            <Box sx={{display: 'flex', gap: 1.25, flexWrap: 'wrap'}}>
              <SecondaryButton
                onClick={resend}
                data-attr="cortile-login-reset-resend"
                disabled={resending || justResent}
              >
                {justResent && 'Email sent'}
                {!justResent && 'Resend email'}
              </SecondaryButton>
              <SecondaryButton
                data-attr="cortile-login-reset-email-change"
                onClick={() => {
                  setSentTo(null);
                  setJustResent(false);
                }}
              >
                Use a different email
              </SecondaryButton>
            </Box>
            {resendError && <FieldError>{resendError}</FieldError>}
          </Box>
          <TextButton
            back
            align="flex-start"
            onClick={() => navigate('/login')}
            data-attr="cortile-login-reset-back"
          >
            Back to log in
          </TextButton>
        </>
      )}
    </CortileAuthLayout>
  );
};
